import { fn } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBreadcrumb,
  PrismButton,
  PrismIcon,
  PrismLoadingBar,
  PrismTooltip,
} from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { and, eq, gt, not, or } from 'ember-truth-helpers';
import { pushAndReturnPayload } from 'frontend-utils/utils/ember-data';
import { formatUrl } from 'frontend-utils/utils/url/format-url';
import type { TableLayoutSettings } from 'lumen/components/hotel/budget-forecast/table/layout-settings';
import HotelBudgetForecastTableLayoutSettings from 'lumen/components/hotel/budget-forecast/table/layout-settings';
import HotelBudgetForecastVarianceOptions from 'lumen/components/hotel/budget-forecast/variance-options';
import SettingsBudgetForecastBannerForceDistribute from 'lumen/components/settings/budget-forecast/banner-force-distribute';
import SettingsBudgetForecastButtonDistribute from 'lumen/components/settings/budget-forecast/button-distribute';
import SettingsBudgetForecastDatepicker from 'lumen/components/settings/budget-forecast/datepicker';
import SettingsBudgetForecastMenuEntry from 'lumen/components/settings/budget-forecast/menu-entry';
import SettingsBudgetForecastModalBulkUpdate from 'lumen/components/settings/budget-forecast/modal-bulk-update';
import SettingsBudgetForecastModalSegmentVisibility from 'lumen/components/settings/budget-forecast/modal-segment-visibility';
import SettingsBudgetForecastModalsModalCopyToExisting from 'lumen/components/settings/budget-forecast/modals/modal-copy-to-existing';
import SettingsBudgetForecastModalsModalDeleteEntry from 'lumen/components/settings/budget-forecast/modals/modal-delete-entry';
import SettingsBudgetForecastModalsModalDuplicateEntry from 'lumen/components/settings/budget-forecast/modals/modal-duplicate-entry';
import SettingsBudgetForecastModalsModalEditEntryTitle from 'lumen/components/settings/budget-forecast/modals/modal-edit-entry-title';
import SettingsBudgetForecastSaveConfirmModal from 'lumen/components/settings/budget-forecast/save-confirm-modal';
import SettingsBudgetForecastTableBudgetForecast from 'lumen/components/settings/budget-forecast/table-budget-forecast';
import SettingsBudgetForecastUploadDownloadModal from 'lumen/components/settings/budget-forecast/upload-download-modal';
import SettingsBudgetForecastVersionHistoryDrawer from 'lumen/components/settings/budget-forecast/version-history-drawer';
import SettingsPageTemplate from 'lumen/components/settings/page-template';
import type {
  BudgetForecastDataItem,
  BudgetForecastEntry,
  BudgetForecastSegment,
} from 'lumen/models';
import type BudgetForecastEntryModel from 'lumen/models/budget-forecast/entry';
import type AjaxService from 'lumen/services/ajax';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import { LOCAL_STORAGE_KEYS } from 'lumen/services/local-storage';
import { groupItemsByDate } from 'lumen/utils/budget-forecast';
import type { CapacityWithMeta } from 'lumen/utils/budget-forecast/data-fetching';
import { markItemsAsNeedsForceDistribution } from 'lumen/utils/budget-forecast/force-distribution';
import {
  type DataType,
  DATE_GRANULARITY,
  type DateGranularity,
  DEFAULT_DISTRIBUTION_METHOD,
  DISTRIBUTION_METHODS,
  type DistributionMethod,
  INTERCOM_HELP_LINK,
  isValidDistributionMethod,
} from 'lumen/utils/constants/budget-forecast-constants';
import { DRAWER_IDS } from 'lumen/utils/constants/general-constants';
import type { Variance } from 'lumen/utils/constants/variance';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';

import styles from './settings-page.module.css';

type DistributeQueryParams = {
  date_aggregation: DateGranularity;
  distribution_method: DistributionMethod;
  entry_id: string;
  subscription_id: string;
};

type SaveQueryParams = {
  distribution_method?: DistributionMethod;
  entry_id: string;
  subscription_id: string;
};

export interface SettingsBudgetForecastSettingsPageSignature {
  Args: {
    budgetForecastItems: BudgetForecastDataItem[];
    capacities: CapacityWithMeta | null | undefined;
    endDate: string;
    entries: BudgetForecastEntryModel[];
    entry: BudgetForecastEntry | null;
    expandedRowIds: string[];
    granularity: DateGranularity;
    isLoading: boolean;
    isLoadingVersions: boolean;
    layoutSettings: TableLayoutSettings;
    onCollapseAllRows: () => void;
    onRowCollapseToggle: (rowId: string) => void;
    onVarianceChange: (variance: Variance) => void;
    reloadData: () => void;
    segmentItems: BudgetForecastSegment[];
    selectedVersionId: string | undefined;
    startDate: string;
    updateLayoutSettings: (settings: TableLayoutSettings) => void;
    variance: Variance;
    view: 'budget' | 'forecast';
  };
}

export default class SettingsBudgetForecastSettingsPage extends Component<SettingsBudgetForecastSettingsPageSignature> {
  @service declare ajax: AjaxService;
  @service declare alerting: Services['alerting'];
  @service declare budgetForecast: Services['budget-forecast'];
  @service declare drawer: Services['drawer'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intercom: Services['intercom'];
  @service declare intl: Services['intl'];
  @service declare localStorage: Services['local-storage'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked dataTypeOrder: DataType[] = [];
  @tracked distributionMethod?: DistributionMethod;
  @tracked duplicateModalType: 'duplicate' | 'duplicate-as-other-type' | null =
    null;
  @tracked hiddenDataTypes: DataType[] = [];
  @tracked isBulkUpdateModalOpen = false;
  @tracked isCopyToExistingModalOpen = false;
  @tracked isDeleteEntryModalOpen = false;
  @tracked isEditTitleModalOpen = false;
  @tracked isSaveModalOpen = false;
  @tracked isSegmentVisibilityModalOpen = false;
  @tracked isUploadDownloadModalOpen = false;

  archiveEntryTask = task(async (): Promise<void> => {
    const { entry } = this.args;

    if (!entry) {
      return;
    }

    try {
      entry.isArchived = true;
      await entry.save({
        adapterOptions: {
          usePatch: true,
        },
      });

      this.toast.success(
        this.intl.t('budget_forecast.archive_entry.success', {
          title: entry.title,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  distributeTask = task(
    { drop: true },
    async (method: DistributionMethod): Promise<void> => {
      if (!this.args.entry) {
        return;
      }

      this.trackDistributionAction(method);
      this.distributionMethod = method;
      this.localStorage.setItem(
        LOCAL_STORAGE_KEYS.BUDGET_FORECAST_DISTRIBUTION_METHOD,
        method,
      );

      const items = this.itemsToBeDistributed;

      const urlPath =
        this.args.view === 'budget'
          ? '/apigateway/v1/app/budgetforecast/budget/distribute'
          : '/apigateway/v1/app/budgetforecast/forecast/distribute';

      const params: DistributeQueryParams = {
        subscription_id: this.hotelAndMarketService.hotel.id!,
        distribution_method: method,
        date_aggregation: this.args.granularity,
        entry_id: this.args.entry.id!,
      };

      const distributeUrl = formatUrl(urlPath, params, this.ajax.host);

      const requestBody = {
        data: items.map((item) => item.serialize({ includeId: true })),
      };

      let distributedItems: BudgetForecastDataItem[];
      try {
        const distributeResponse = await this.ajax.request<{ data: object[] }>(
          distributeUrl,
          {
            type: 'POST',
            data: JSON.stringify(requestBody),
          },
        );

        distributedItems = pushAndReturnPayload(
          this.store,
          distributeResponse,
          'budget-forecast/data-item',
        )['data'] as BudgetForecastDataItem[];
      } catch (error: unknown) {
        this.toast.error(
          this.intl.t('settings.budget_forecast.toast.distribute_failed'),
          {
            showIcon: true,
          },
        );
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }

      items.forEach((item) => (item.needsForceDistribution = false));
      distributedItems.forEach((item) => {
        // If you change a lowest-granularity item, then change its parent and click Distribute.
        // We want the lowest-granularity item to have the new distributed value instead of the manually changed value.
        item.rollbackAttributes();

        item.wasChangedByPushPayload = true;
      });

      const distributedItemIds = new Set(distributedItems.map((i) => i.id!));
      this.markNonDistributedItemsAsHidden(items, distributedItemIds);
    },
  );

  lockEntryTask = task(async (): Promise<void> => {
    const { entry } = this.args;

    if (!entry) {
      return;
    }

    try {
      entry.isLocked = true;
      await entry.save({ adapterOptions: { usePatch: true } });

      this.toast.success(
        this.intl.t('budget_forecast.lock_entry.success', {
          title: entry.titleWithDefault,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  restoreEntryTask = task(async (): Promise<void> => {
    const { entry } = this.args;

    if (!entry) {
      return;
    }

    try {
      entry.isArchived = false;
      await entry.save({ adapterOptions: { usePatch: true } });

      this.toast.success(
        this.intl.t('budget_forecast.restore_entry.success', {
          title: entry.title,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  restoreEntryVersionTask = task(async (): Promise<void> => {
    const { entry, selectedVersionId } = this.args;

    if (!entry || !selectedVersionId) {
      return;
    }

    try {
      const baseUrl = `/apigateway/v1/app/budgetforecast/entries/${entry.id}/versions/${selectedVersionId}/restore`;
      const queryParams = {
        subscription_id: this.hotelAndMarketService.hotel.id!,
      };

      const url = formatUrl(baseUrl, queryParams, this.ajax.host);
      const restoreResponse = await this.ajax.request(url, {
        method: 'POST',
      });

      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      this.store.pushPayload('budget-forecast/version', restoreResponse);

      this.toast.success(
        this.intl.t('budget_forecast.restore_version.success'),
        {
          showIcon: true,
        },
      );

      this.router.transitionTo({ queryParams: { versionId: undefined } });
    } catch (error) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  saveTask = task(
    { drop: true },
    async (method?: DistributionMethod): Promise<void> => {
      if (!this.args.entry) {
        return;
      }

      const urlPath =
        this.args.view === 'budget'
          ? '/apigateway/v1/app/budgetforecast/budget/save'
          : '/apigateway/v1/app/budgetforecast/forecast/save';

      const params: SaveQueryParams = {
        subscription_id: this.hotelAndMarketService.hotel.id!,
        entry_id: this.args.entry.id!,
      };

      if (method) {
        params.distribution_method = method;
      }

      const saveUrl = formatUrl(urlPath, params, this.ajax.host);
      const itemsToSave = this.getItemsToSave(this.args.granularity);

      const requestBody = {
        data: itemsToSave.map((item) => item.serialize({ includeId: true })),
      };

      try {
        const saveResponse = await this.ajax.request(saveUrl, {
          type: 'POST',
          data: JSON.stringify(requestBody),
        });

        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.store.pushPayload('budget-forecast/version', saveResponse);

        const successMessage =
          this.args.view === 'budget'
            ? this.intl.t('settings.budget.toast.save_successful')
            : this.intl.t('settings.forecast.toast.save_successful');
        this.toast.success(successMessage, { showIcon: true });

        this.closeSaveModal();
        this.args.reloadData();
      } catch (error: unknown) {
        this.toast.error(this.intl.t('general.toast.something_went_wrong'), {
          showIcon: true,
        });
        this.errorTracking.captureException(error);

        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  unlockEntryTask = task(async (): Promise<void> => {
    const { entry } = this.args;

    if (!entry) {
      return;
    }

    try {
      entry.isLocked = false;
      await entry.save({ adapterOptions: { usePatch: true } });

      this.toast.success(
        this.intl.t('budget_forecast.unlock_entry.success', {
          title: entry.titleWithDefault,
        }),
        { showIcon: true },
      );
    } catch (error) {
      entry.rollbackAttributes();

      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
    }
  });

  get canShowRestorePastVersion(): boolean {
    const { entry, selectedVersionId } = this.args;

    if (!selectedVersionId || !entry) {
      return false;
    }

    return entry.canEdit && !entry.isArchived && !entry.isLocked;
  }

  /**
   * Secondary actions are bulk edit, upload/download, distribute, force distribution banner, and edited values legend.
   */
  get canShowSecondaryActions(): boolean {
    const { entry, selectedVersionId } = this.args;

    if (selectedVersionId || !entry) {
      return false;
    }

    return !entry.isArchived && !entry.isLocked && entry.canEdit;
  }

  get hasItemsWithUndistributedChanges(): boolean {
    return this.budgetForecast.itemsWithUndistributedChanges.length > 0;
  }

  get hasUnsavedChanges(): boolean {
    return (
      this.budgetForecast.editableItems.find(
        (item) => item.hasUnsavedChanges,
      ) !== undefined
    );
  }

  get isDiscardButtonVisible(): boolean {
    if (this.args.isLoading) {
      return false;
    }

    return this.hasItemsWithUndistributedChanges || this.hasUnsavedChanges;
  }

  get isDistributeButtonDisabled(): boolean {
    if (this.args.isLoading || this.distributeTask.isRunning) {
      return true;
    }

    return this.itemsToBeDistributed.length === 0;
  }

  /**
   * Checks if every day/month has at least 1 final LY item, with data, for a segment that is visible in the current view.
   * If this is not the case, we disable the "Distribute using LY finals" option.
   */
  get isDistributeUsingLastYearFinalsDisabled(): boolean {
    if (this.args.isLoading) {
      return false;
    }

    const lastYearFinalItemsWithSegment = this.args.budgetForecastItems.filter(
      (item) =>
        item.dataType === 'final_ly' && item.segment && item.segment.visible,
    );

    if (lastYearFinalItemsWithSegment.length === 0) {
      return true;
    }

    const itemsByDate = groupItemsByDate(lastYearFinalItemsWithSegment);
    const allDatesHaveData = Object.values(itemsByDate).every((items) =>
      items.some((item) => item.hasData),
    );

    return !allDatesHaveData;
  }

  get isSaveButtonDisabled(): boolean {
    if (this.args.isLoading || this.saveTask.isRunning) {
      return true;
    }

    return this.itemsToBeDistributed.length !== 0 || !this.hasUnsavedChanges;
  }

  get itemsToBeDistributed(): BudgetForecastDataItem[] {
    if (this.budgetForecast.itemsToBeForceDistributed.length > 0) {
      return this.budgetForecast.itemsToBeForceDistributed;
    }

    return this.budgetForecast.itemsWithUndistributedChanges;
  }

  get lastUsedDistributionMethod(): DistributionMethod {
    const savedDistributionMethod = this.localStorage.getItem(
      LOCAL_STORAGE_KEYS.BUDGET_FORECAST_DISTRIBUTION_METHOD,
    );

    return savedDistributionMethod &&
      isValidDistributionMethod(savedDistributionMethod)
      ? savedDistributionMethod
      : DEFAULT_DISTRIBUTION_METHOD;
  }

  get monthlyTotalsOverrideBannerLink(): {
    action: () => void;
    icon: boolean;
    text: string;
  } {
    return {
      text: this.intl.t('general.learn_more'),
      action: (): void =>
        this.intercom.redirectToIntercomHelp(INTERCOM_HELP_LINK),
      icon: true,
    };
  }

  get selectedDistributionMethod(): DistributionMethod {
    return this.distributionMethod ?? this.lastUsedDistributionMethod;
  }

  getItemsToSave(granularity: DateGranularity): BudgetForecastDataItem[] {
    if (granularity === DATE_GRANULARITY.DAY) {
      return this.budgetForecast.editableItemsWithoutHidden;
    }

    const changedMonthsSet = new Set(
      this.budgetForecast.editableItemsWithoutHidden
        .filter((item) => item.hasUnsavedChanges)
        .map((item) => item.date.format('YYYY-MM')),
    );
    return this.budgetForecast.editableItemsWithoutHidden.filter((item) =>
      changedMonthsSet.has(item.date.format('YYYY-MM')),
    );
  }

  /**
   * Utility function to mark all budget/forecast items for a specific segment key as hidden.
   */
  markItemsForSegmentKeyAsHidden(segmentKey: string): void {
    const itemsForSegmentKey =
      this.budgetForecast.editableItemsGroupedBySegmentKey[segmentKey];

    if (itemsForSegmentKey) {
      itemsForSegmentKey.forEach(
        (groupedItem) => (groupedItem.isItemHidden = true),
      );
    }
  }

  /**
   * Utility function to mark budget/forecast items as hidden.
   * They should become hidden if they were not in the /distribute response.
   *
   * This happens when there was data for a hidden/deleted segment and the user
   * distributes a parent segment.
   */
  markNonDistributedItemsAsHidden(
    items: BudgetForecastDataItem[],
    distributedItemIds: Set<string>,
  ): void {
    items.forEach((item) => {
      if (!distributedItemIds.has(item.id!)) {
        this.markItemsForSegmentKeyAsHidden(item.segmentKey);
      }

      this.markNonDistributedItemsAsHidden(item.children, distributedItemIds);
    });
  }

  @action
  closeBulkUpdateModal(): void {
    this.isBulkUpdateModalOpen = false;
  }

  @action
  closeCopyToExistingModal(): void {
    this.isCopyToExistingModalOpen = false;
  }

  @action
  closeDeleteEntryModal(wasDeleted: boolean = false): void {
    if (!wasDeleted) {
      this.isDeleteEntryModalOpen = false;
      return;
    }

    const route =
      this.args.view === 'budget'
        ? 'hotel.settings.budget'
        : 'hotel.settings.forecast';
    this.router.transitionTo(route);
  }

  @action
  closeDuplicateModal(): void {
    this.duplicateModalType = null;
  }

  @action
  closeEditTitleModal(): void {
    this.isEditTitleModalOpen = false;
  }

  @action
  closeSaveModal(): void {
    this.isSaveModalOpen = false;
  }

  @action
  closeSegmentVisibilityModal(wereChangesMade: boolean): void {
    this.isSegmentVisibilityModalOpen = false;

    if (wereChangesMade) {
      this.args.reloadData();
    }
  }

  @action
  closeUploadDownloadModal(shouldReloadData: boolean = false): void {
    this.isUploadDownloadModalOpen = false;

    if (shouldReloadData) {
      this.args.reloadData();
    }
  }

  @action
  async discardChanges(): Promise<void> {
    const isDiscardConfirmed = await this.alerting.showConfirmWarningAlert({
      title:
        this.args.view === 'budget'
          ? this.intl.t('budget_forecast.budget.discard_changes.title')
          : this.intl.t('budget_forecast.forecast.discard_changes.title'),
      text:
        this.args.view === 'budget'
          ? this.intl.t('budget_forecast.budget.discard_changes.description')
          : this.intl.t('budget_forecast.forecast.discard_changes.description'),
      buttons: {
        cancel: {
          text: this.intl.t('general.keep_editing'),
        },
        confirm: { text: this.intl.t('general.discard_changes') },
      },
    });

    if (!isDiscardConfirmed) {
      return;
    }

    this.usageAnalytics.track('Changes Discarded', {
      eventTrigger: 'discard-changes-clicked',
    });

    // Since we use store.pushPayload to update the model instances,
    // rollbackAttributes does not reset the model to the initial state.
    // When there are distributed, but unsaved changes (changes that were pushed to the store),
    // we need to refresh the model.
    if (
      this.budgetForecast.editableItems.some(
        (item) => item.wasChangedByPushPayload,
      )
    ) {
      this.args.reloadData();
      return;
    }

    const { editableItems } = this.budgetForecast;

    editableItems.forEach((item) => item.rollbackAttributes());
    markItemsAsNeedsForceDistribution(editableItems);
  }

  @action
  handleDateRangeChange(
    granularity: DateGranularity,
    startDate: string,
    endDate: string,
  ): void {
    this.router.transitionTo({
      queryParams: { granularity, startDate, endDate },
    });
  }

  @action
  handleSaveClick(): void {
    if (this.args.granularity === DATE_GRANULARITY.DAY) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.saveTask.perform();
    } else {
      this.isSaveModalOpen = true;
    }

    if (this.args.view === 'budget') {
      this.usageAnalytics.track('Budget Saved', {
        eventTrigger: 'save-budget-clicked',
      });
    } else {
      this.usageAnalytics.track('Forecast Saved', {
        eventTrigger: 'save-forecast-clicked',
      });
    }
  }

  @action
  isEntryTitleUnique(title: string): boolean {
    const { entry, entries } = this.args;

    if (!entry) {
      return true;
    }

    const existingTitles = new Set(
      entries.map((e) => e.titleWithDefault.trim().toLowerCase()),
    );
    return !existingTitles.has(title.trim().toLowerCase());
  }

  @action
  onDuplicate(): void {
    this.duplicateModalType = 'duplicate';
  }

  @action
  onDuplicateAsOtherType(): void {
    this.duplicateModalType = 'duplicate-as-other-type';
  }

  @action
  onSelectVersion(versionId: string | undefined): void {
    this.router.transitionTo({
      queryParams: { versionId },
    });
  }

  @action
  openBulkUpdateModal(): void {
    this.isBulkUpdateModalOpen = true;
    this.usageAnalytics.track('Bulk Edit Opened', {
      eventTrigger: 'bulk-edit-button-clicked',
    });
  }

  @action
  openVersionHistoryDrawer(): void {
    this.drawer.expandDrawer(DRAWER_IDS.BUDGET_FORECAST_VERSION_HISTORY);
  }

  @action
  trackDistributionAction(method: DistributionMethod): void {
    if (method === DISTRIBUTION_METHODS.EQUAL) {
      this.usageAnalytics.track('Data Spread Evenly', {
        eventTrigger: 'distribute-evenly-clicked',
      });
    } else {
      this.usageAnalytics.track('Data Distributed by LY', {
        eventTrigger: 'distribute-using-LY-finals-clicked',
      });
    }
  }

  <template>
    {{#if
      (or @isLoading this.distributeTask.isRunning this.saveTask.isRunning)
    }}
      <PrismLoadingBar />
    {{/if}}

    <SettingsPageTemplate
      @badge={{if @entry.isMain (t "budget_forecast.main")}}
      data-test-id="budget-forecast-settings-page"
    >
      <:title>
        {{#if @entry}}
          <div class="flex items-center gap-200">
            {{#if @entry.isArchived}}
              <PrismIcon @name="archive" data-test-icon="archive" />
            {{/if}}

            {{#if @entry.isLocked}}
              <span>
                <PrismIcon @name="lock" data-test-icon="lock" />
                <PrismTooltip
                  @text={{if
                    (eq @entry.dataType "budget")
                    (t "budget_forecast.budget.locked_message")
                    (t "budget_forecast.forecast.locked_message")
                  }}
                />
              </span>
            {{/if}}

            <h2>{{@entry.titleWithDefault}}</h2>

            {{#if @selectedVersionId}}
              <PrismIcon @name="history" data-test-icon="history" />
            {{/if}}
          </div>
        {{else}}
          <div class="loading-pulse w-[100px]"></div>
        {{/if}}
      </:title>
      <:breadcrumb>
        <PrismBreadcrumb as |B|>
          <B.item
            @label={{if
              (eq @view "budget")
              (t "settings.budget.index.title")
              (t "settings.forecast.index.title")
            }}
            @route={{if
              (eq @view "budget")
              "hotel.settings.budget.index"
              "hotel.settings.forecast.index"
            }}
          />
          <B.item
            @isCurrent={{true}}
            @label={{if
              (eq @view "budget")
              (t "budget_forecast.budget.edit")
              (t "budget_forecast.forecast.edit")
            }}
          />
        </PrismBreadcrumb>
      </:breadcrumb>
      <:headerAfter>
        <div class="flex items-center gap-200">
          {{#if @entry}}
            <SettingsBudgetForecastMenuEntry
              @archiveEntry={{this.archiveEntryTask.perform}}
              @copyToExisting={{fn (mut this.isCopyToExistingModalOpen) true}}
              @deleteEntry={{fn (mut this.isDeleteEntryModalOpen) true}}
              @duplicateEntry={{this.onDuplicate}}
              @duplicateEntryAsOtherType={{this.onDuplicateAsOtherType}}
              @editEntryTitle={{fn (mut this.isEditTitleModalOpen) true}}
              @entry={{@entry}}
              @isDisabled={{or
                this.hasItemsWithUndistributedChanges
                this.hasUnsavedChanges
              }}
              @isLatestVersionSelected={{eq @selectedVersionId undefined}}
              @lockEntry={{this.lockEntryTask.perform}}
              @viewVersionHistory={{this.openVersionHistoryDrawer}}
            />
          {{/if}}

          {{#if this.isDiscardButtonVisible}}
            <PrismButton
              @buttonType="critical-secondary"
              @isDisabled={{or
                this.distributeTask.isRunning
                this.saveTask.isRunning
              }}
              @label={{t "general.discard_changes"}}
              @onClick={{this.discardChanges}}
              data-test-id="button-discard-changes"
            />
          {{/if}}

          {{#if @selectedVersionId}}
            <PrismButton
              @buttonType="secondary"
              @label={{t "budget_forecast.back_to_current_version"}}
              @onClick={{fn this.onSelectVersion undefined}}
              data-test-button="back-to-current-version"
            />

            {{#if this.canShowRestorePastVersion}}
              <PrismButton
                @iconBefore="undo"
                @isDisabled={{this.restoreEntryVersionTask.isRunning}}
                @isLoading={{this.restoreEntryVersionTask.isRunning}}
                @label={{t "budget_forecast.restore_version"}}
                @onClick={{this.restoreEntryVersionTask.perform}}
                data-test-button="restore-version"
              />
            {{/if}}

          {{/if}}

          {{#if (and @entry.canEdit (not @selectedVersionId))}}
            {{#if @entry.isArchived}}
              <PrismButton
                @buttonType="secondary"
                @isDisabled={{this.restoreEntryTask.isRunning}}
                @isLoading={{this.restoreEntryTask.isRunning}}
                @label={{if
                  (eq @view "budget")
                  (t "budget_forecast.budget.restore")
                  (t "budget_forecast.forecast.restore")
                }}
                @onClick={{this.restoreEntryTask.perform}}
                data-test-button="restore-entry"
              />
            {{else if @entry.isLocked}}
              <PrismButton
                @buttonType="secondary"
                @isDisabled={{this.unlockEntryTask.isRunning}}
                @isLoading={{this.unlockEntryTask.isRunning}}
                @label={{if
                  (eq @view "budget")
                  (t "budget_forecast.budget.unlock")
                  (t "budget_forecast.forecast.unlock")
                }}
                @onClick={{this.unlockEntryTask.perform}}
                data-test-button="unlock-entry"
              />
            {{else}}
              <PrismButton
                @isDisabled={{this.isSaveButtonDisabled}}
                @isLoading={{if
                  this.isSaveModalOpen
                  false
                  this.saveTask.isRunning
                }}
                @label={{if
                  (eq @view "budget")
                  (t "budget_forecast.save_budget")
                  (t "budget_forecast.save_forecast")
                }}
                @onClick={{this.handleSaveClick}}
                data-test-id="button-save"
              />
            {{/if}}
          {{/if}}
        </div>
      </:headerAfter>
      <:default>
        <div class="flex flex-col gap-400">
          <div class="flex justify-between">
            <div class="flex gap-400">
              <SettingsBudgetForecastDatepicker
                @disabledTooltipMessage={{if
                  (eq @view "budget")
                  (t "budget_forecast.budget.datepicker_disabled")
                  (t "budget_forecast.forecast.datepicker_disabled")
                }}
                @endDate={{@endDate}}
                @granularity={{@granularity}}
                @handleDateRangeChange={{this.handleDateRangeChange}}
                @isDisabled={{or
                  this.hasItemsWithUndistributedChanges
                  this.hasUnsavedChanges
                }}
                @startDate={{@startDate}}
              />
              <HotelBudgetForecastVarianceOptions
                @onChange={{@onVarianceChange}}
                @selectedVariance={{@variance}}
              />
              <HotelBudgetForecastTableLayoutSettings
                @baseDataType={{@view}}
                @dataTypesOrder={{@layoutSettings.dataTypesOrder}}
                @hiddenDataTypes={{@layoutSettings.hiddenDataTypes}}
                @isDisabled={{@isLoading}}
                @isEditMode={{true}}
                @onConfirm={{@updateLayoutSettings}}
              />
            </div>

            {{#if this.canShowSecondaryActions}}
              <div class="flex gap-200">
                <PrismButton
                  @buttonType="secondary"
                  @iconBefore="edit-pencil"
                  @isDisabled={{or
                    @isLoading
                    (gt this.itemsToBeDistributed.length 0)
                  }}
                  @label={{t "general.bulk_update"}}
                  @onClick={{this.openBulkUpdateModal}}
                  class="ic-product-tour-49285022-bulk-update-button"
                  data-test-id="button-bulk-edit"
                />

                <PrismButton
                  @buttonType="secondary"
                  @iconBefore="upload"
                  @label={{if
                    (eq @view "budget")
                    (t "budget_forecast.upload_budget.title")
                    (t "budget_forecast.upload_forecast.title")
                  }}
                  @onClick={{fn (mut this.isUploadDownloadModalOpen) true}}
                  class="ic-product-tour-49285022-upload-download-button"
                  data-test-button="upload-download"
                />

                <SettingsBudgetForecastButtonDistribute
                  @disableLastYearFinals={{this.isDistributeUsingLastYearFinalsDisabled}}
                  @distributionMethod={{this.selectedDistributionMethod}}
                  @isDisabled={{this.isDistributeButtonDisabled}}
                  @isLoading={{this.distributeTask.isRunning}}
                  @onDistribute={{this.distributeTask.perform}}
                />
              </div>
            {{/if}}
          </div>

          {{#if (and this.canShowSecondaryActions (not @isLoading))}}
            <SettingsBudgetForecastBannerForceDistribute
              @itemsToBeForceDistributed={{this.budgetForecast.itemsToBeForceDistributed}}
              @view={{@view}}
            />
          {{/if}}

          <div class="flex flex-col gap-100">
            {{#if this.canShowSecondaryActions}}
              <span class="text-200--medium flex items-center gap-100 self-end">
                <div class={{styles.edited-value-bullet}}></div>
                {{t "budget_forecast.edited_values"}}
              </span>
            {{/if}}
            <SettingsBudgetForecastTableBudgetForecast
              @capacities={{@capacities}}
              @data={{@budgetForecastItems}}
              @entry={{@entry}}
              @expandedRowIds={{@expandedRowIds}}
              @isDisabled={{or
                @entry.isArchived
                @entry.isLocked
                this.saveTask.isRunning
              }}
              @isLatestVersionSelected={{eq @selectedVersionId undefined}}
              @isLoading={{@isLoading}}
              @isSegmentVisibilityEditButtonDisabled={{or
                @entry.isArchived
                @entry.isLocked
                this.hasItemsWithUndistributedChanges
                this.hasUnsavedChanges
              }}
              @layoutSettings={{@layoutSettings}}
              @onCollapseAllRows={{@onCollapseAllRows}}
              @onRowCollapseToggle={{@onRowCollapseToggle}}
              @openSegmentVisibilityModal={{fn
                (mut this.isSegmentVisibilityModalOpen)
                true
              }}
              @variance={{@variance}}
              @view={{@view}}
            />
          </div>
        </div>
      </:default>
    </SettingsPageTemplate>

    {{#if @entry}}
      <SettingsBudgetForecastVersionHistoryDrawer
        @isLoading={{@isLoadingVersions}}
        @onSelectVersion={{this.onSelectVersion}}
        @selectedVersionId={{@selectedVersionId}}
        @versions={{@entry.versions}}
      />

      {{#if this.isSegmentVisibilityModalOpen}}
        <SettingsBudgetForecastModalSegmentVisibility
          @entry={{@entry}}
          @onClose={{this.closeSegmentVisibilityModal}}
          @segments={{@segmentItems}}
          @view={{@view}}
        />
      {{/if}}

      {{#if this.isUploadDownloadModalOpen}}
        <SettingsBudgetForecastUploadDownloadModal
          @entry={{@entry}}
          @onClose={{this.closeUploadDownloadModal}}
          @view={{@view}}
        />
      {{/if}}

      {{#if this.isBulkUpdateModalOpen}}
        <SettingsBudgetForecastModalBulkUpdate
          @data={{this.budgetForecast.editableItems}}
          @endDate={{@endDate}}
          @entry={{@entry}}
          @granularity={{@granularity}}
          @onClose={{this.closeBulkUpdateModal}}
          @segments={{@segmentItems}}
          @startDate={{@startDate}}
          @view={{@view}}
        />
      {{/if}}
    {{/if}}

    {{#if this.isSaveModalOpen}}
      <SettingsBudgetForecastSaveConfirmModal
        @disableLastYearFinals={{this.isDistributeUsingLastYearFinalsDisabled}}
        @distributionMethod={{this.selectedDistributionMethod}}
        @isSaveTaskRunning={{this.saveTask.isRunning}}
        @onCancel={{this.closeSaveModal}}
        @onConfirm={{this.saveTask.perform}}
      />
    {{/if}}

    {{#if @entry}}
      {{#if this.isEditTitleModalOpen}}
        <SettingsBudgetForecastModalsModalEditEntryTitle
          @entry={{@entry}}
          @isTitleUnique={{this.isEntryTitleUnique}}
          @onClose={{this.closeEditTitleModal}}
        />
      {{/if}}

      {{#if this.isDeleteEntryModalOpen}}
        <SettingsBudgetForecastModalsModalDeleteEntry
          @entry={{@entry}}
          @onClose={{this.closeDeleteEntryModal}}
        />
      {{/if}}

      {{#if this.duplicateModalType}}
        <SettingsBudgetForecastModalsModalDuplicateEntry
          @entry={{@entry}}
          @isDuplicateAsOtherType={{eq
            this.duplicateModalType
            "duplicate-as-other-type"
          }}
          @isTitleUnique={{this.isEntryTitleUnique}}
          @onClose={{this.closeDuplicateModal}}
        />
      {{/if}}

      {{#if this.isCopyToExistingModalOpen}}
        <SettingsBudgetForecastModalsModalCopyToExisting
          @entry={{@entry}}
          @onClose={{this.closeCopyToExistingModal}}
        />
      {{/if}}
    {{/if}}
  </template>
}
